import type * as React from 'react';
import { useMemo, useState } from 'react';

/**
 * The state and the keyboard of a combobox with a listbox popup, in one place.
 *
 * **There are two of these controls in the registration form and there is one behaviour.** The
 * country field and the telephone prefix both offer a couple of hundred options that cannot be
 * scanned and that a platform `<select>`'s prefix-only type-ahead cannot reach, so both are ARIA
 * comboboxes; what differs between them is what an option LOOKS like and what counts as a match,
 * not how arrow keys move or when the list closes. Keeping the behaviour here means the keyboard
 * contract the product promises exists once and is tested once, and it is also what the lint gate
 * requires: two copies of the same twenty-five-line handler is a finding, and rightly so.
 *
 * **The caller owns the options and the matcher; this owns the interaction.** The matcher is passed
 * in rather than the matches, because the query lives here — and it must be a module-level function
 * so the memo below has a stable dependency.
 */

/** What a combobox needs of an option: a stable value to key, choose and compare by. */
export interface ComboboxOption {
  readonly value: string;
}

/** Everything a rendering needs, with no opinion about what it renders. */
export interface Combobox<T extends ComboboxOption> {
  /** Whether the listbox is showing. */
  readonly open: boolean;
  /**
   * What has been typed, or `null` for "showing the selection".
   *
   * The distinction is load-bearing: without it, clearing the box would read as clearing the
   * choice, and choosing an option would have to overwrite whatever was half-typed.
   */
  readonly query: string | null;
  /** The options to offer, already narrowed and capped by the caller's matcher. */
  readonly matches: readonly T[];
  /** The active option, which is what `aria-activedescendant` points at and Enter takes. */
  readonly highlighted: T | null;
  readonly onQueryChange: (next: string) => void;
  readonly onFocus: () => void;
  readonly onBlur: () => void;
  readonly onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  /** Takes an option: used by the pointer path, which the keyboard path shares. */
  readonly choose: (option: T | null) => void;
}

export function useCombobox<T extends ComboboxOption>(
  options: readonly T[],
  matcher: (options: readonly T[], query: string | null) => readonly T[],
  pick: (option: T) => void,
): Combobox<T> {
  const [query, setQuery] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  const matches = useMemo(() => matcher(options, query), [matcher, options, query]);
  const highlighted = matches[Math.min(active, Math.max(matches.length - 1, 0))] ?? null;

  function choose(option: T | null): void {
    if (option === null) {
      return;
    }
    pick(option);
    setQuery(null);
    setOpen(false);
  }

  function close(): void {
    setOpen(false);
    setQuery(null);
  }

  /**
   * The keyboard, which is the whole reason these controls are not inputs with a `datalist`.
   *
   * This product is worked from the keyboard at a gate. A country that cannot be set without a
   * mouse is left blank, and `country_of_origin` is one of AD-18's two duplicate-detection keys, so
   * a blank one leaves records that silently defeat duplicate detection.
   */
  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'Escape') {
      close();
      return;
    }
    if (event.key === 'Enter' && open) {
      event.preventDefault();
      choose(highlighted);
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
      setActive((held) => {
        const step = event.key === 'ArrowDown' ? 1 : -1;
        const next = held + step;
        if (next < 0) {
          return Math.max(matches.length - 1, 0);
        }
        return next >= matches.length ? 0 : next;
      });
      return;
    }
    if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      setOpen(true);
      setActive(event.key === 'Home' ? 0 : Math.max(matches.length - 1, 0));
    }
  }

  return {
    open,
    query,
    matches,
    highlighted,
    onQueryChange: (next: string) => {
      setQuery(next);
      setActive(0);
      setOpen(true);
    },
    onFocus: () => {
      setOpen(true);
    },
    // Closing on blur rather than on a deferred timer: an option is chosen on `mousedown`, which
    // fires BEFORE blur, so there is nothing to wait for — and a timer doing the waiting is cleared
    // by the input's own keydown, which cannot run after a blur.
    onBlur: close,
    onKeyDown,
    choose,
  };
}
